/**
 * RichTextEditor Component
 * 
 * A rich text editor built with TipTap, supporting markdown-style formatting,
 * images, and extensible for file attachments.
 * 
 * @example Basic Usage
 * ```tsx
 * <RichTextEditor
 *   content={message}
 *   onChange={setMessage}
 *   onSubmit={handleSend}
 *   placeholder="Type your message..."
 * />
 * ```
 * 
 * @example With File Upload
 * ```tsx
 * const handleFileUpload = async (file: File) => {
 *   const formData = new FormData();
 *   formData.append('file', file);
 *   const response = await fetch('/api/upload', { method: 'POST', body: formData });
 *   const data = await response.json();
 *   return data.url; // Return the uploaded file URL
 * };
 * 
 * <RichTextEditor
 *   content={message}
 *   onChange={setMessage}
 *   onFileUpload={handleFileUpload}
 *   enableFileAttachments={true}
 * />
 * ```
 * 
 * Future Enhancement: Custom File Attachment Node
 * To create a proper file attachment display (not just links), you can:
 * 1. Create a custom TipTap node extension for file attachments
 * 2. Add file metadata (name, size, type, download URL)
 * 3. Render custom UI for file attachments with icons and download buttons
 * 
 * See: https://tiptap.dev/docs/editor/extensions/custom-extensions/create-new-extensions
 */
import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ListItem from '@tiptap/extension-list-item';
import Placeholder from '@tiptap/extension-placeholder';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import HardBreak from '@tiptap/extension-hard-break';
import { Markdown } from 'tiptap-markdown';
import { cn } from '../../lib/utils';
import { 
  Bold, 
  Italic, 
  List, 
  ListOrdered, 
  Link as LinkIcon,
  Image as ImageIcon,
  Paperclip
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  onSubmit?: () => void;
  // Cmd/Ctrl+Enter, for a secondary action alongside Enter-to-submit.
  onModSubmit?: () => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  showToolbar?: boolean;
  // Rendered at the right end of the toolbar row (e.g. a mode badge).
  toolbarEnd?: React.ReactNode;
  /** Callback for handling file uploads. Should return the uploaded attachment metadata. */
  onFileUpload?: (file: File) => Promise<{id: string, filename: string, size: number, type: string, url: string}>;
  /** Enable file attachment button in toolbar. Requires onFileUpload to be provided. */
  enableFileAttachments?: boolean;
  /** Callback when files are being uploaded (for loading states) */
  onUploadStart?: () => void;
  /** Callback when files finish uploading */
  onUploadEnd?: () => void;
}

// Custom HardBreak behavior for Slack-like Shift+Enter
const CustomHardBreak = HardBreak.extend({
  addKeyboardShortcuts() {
    return {
      'Shift-Enter': () => {
        const editor = this.editor;
        // If inside a list, create a new list item; otherwise split the block
        if (editor.isActive('bulletList') || editor.isActive('orderedList')) {
          return editor.commands.splitListItem('listItem');
        }
        return editor.commands.splitBlock();
      },
    };
  },
});

// Override ListItem to ensure Shift+Enter creates a new list item (Slack-like)
// Double Shift+Enter on empty list item exits the list
const CustomListItem = ListItem.extend({
  addKeyboardShortcuts() {
    return {
      'Shift-Enter': () => {
        const { state } = this.editor;
        const { $from, empty } = state.selection;
        
        // Find the list item node by searching up the tree
        let depth = $from.depth;
        let listItemNode = null;
        
        while (depth > 0) {
          const node = $from.node(depth);
          if (node.type.name === this.name) {
            listItemNode = node;
            break;
          }
          depth--;
        }
        
        // If we're in an empty list item (no content), exit the list
        if (listItemNode && empty && listItemNode.textContent.trim() === '') {
          return this.editor.commands.liftListItem(this.name);
        }
        
        // Otherwise, create a new list item
        return this.editor.commands.splitListItem(this.name);
      },
    };
  },
});

const ALLOWED_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

const normalizeLinkHref = (href: string): string | null => {
  const trimmedHref = href.trim();

  if (!trimmedHref || trimmedHref.startsWith('#')) {
    return trimmedHref;
  }

  const protocolMatch = trimmedHref.match(/^([a-z][a-z\d+.-]*:)/i);
  if (protocolMatch) {
    return ALLOWED_LINK_PROTOCOLS.has(protocolMatch[1].toLowerCase()) ? trimmedHref : null;
  }

  return `https://${trimmedHref}`;
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  onSubmit,
  onModSubmit,
  placeholder = 'Type your message...',
  className,
  disabled = false,
  showToolbar = true,
  toolbarEnd,
  onFileUpload,
  enableFileAttachments = false,
  onUploadStart,
  onUploadEnd,
}) => {
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkHref, setLinkHref] = useState('');
  const [linkError, setLinkError] = useState('');
  const linkSelectionRef = useRef<{ from: number; to: number } | null>(null);
  const initialLinkTextRef = useRef('');
  const initialLinkHrefRef = useRef('');
  const linkHrefInputRef = useRef<HTMLInputElement>(null);

  const handlePasteFile = async (file: File) => {
    if (!onFileUpload) return;
    
    try {
      if (onUploadStart) onUploadStart();
      await onFileUpload(file);
      // Don't insert into editor - parent component will handle showing as chips
    } catch (error) {
      console.error('Failed to upload pasted file:', error);
    } finally {
      if (onUploadEnd) onUploadEnd();
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        hardBreak: false, // Disable default hard break
        listItem: false, // We'll provide our custom listItem
      }),
      CustomListItem,
      CustomHardBreak, // Use our custom one that makes Shift+Enter = new paragraph
      Markdown,
      Placeholder.configure({
        placeholder,
      }),
      Link.configure({
        openOnClick: false,
      }),
      Image,
    ],
    content,
    editorProps: {
      attributes: {
        class: cn(
          'prose prose-sm max-w-none focus:outline-none min-h-[60px] max-h-[200px] overflow-y-auto',
          'dark:prose-invert',
          disabled && 'opacity-50 cursor-not-allowed'
        ),
      },
      handleKeyDown: (_view, event) => {
        if (disabled || event.key !== 'Enter' || event.isComposing) return false;
        // Cmd/Ctrl+Enter: the secondary action, when the caller has one.
        if ((event.metaKey || event.ctrlKey) && onModSubmit) {
          event.preventDefault();
          onModSubmit();
          return true;
        }
        // Send on Enter; use Shift+Enter for new lines/bullets (Slack-style)
        if (!event.shiftKey && !event.metaKey && !event.ctrlKey) {
          event.preventDefault();
          if (onSubmit) {
            onSubmit();
          }
          return true;
        }
        return false;
      },
      handlePaste: (view, event) => {
        // Handle pasted files (images from clipboard)
        if (!onFileUpload || disabled) return false;
        
        const items = event.clipboardData?.items;
        if (!items) return false;
        
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.type.indexOf('image') !== -1) {
            const file = item.getAsFile();
            if (file) {
              event.preventDefault();
              handlePasteFile(file);
              return true;
            }
          }
        }
        
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      const markdown = (editor.storage as any).markdown.getMarkdown();
      onChange(markdown);
    },
    editable: !disabled,
  });

  // Update editor content when prop changes externally (e.g., after sending message)
  useEffect(() => {
    if (editor && content === '' && (editor.storage as any).markdown.getMarkdown() !== '') {
      editor.commands.setContent('');
    }
  }, [content, editor]);

  useEffect(() => {
    if (!linkPopoverOpen) return;

    const frameId = window.requestAnimationFrame(() => {
      linkHrefInputRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [linkPopoverOpen]);

  useEffect(() => {
    if (disabled) {
      setLinkPopoverOpen(false);
    }
  }, [disabled]);

  // `editable` is only read when the editor is created. The rep composer now
  // mounts before its socket is open (disabled), so it must be re-enabled
  // when the prop flips, or it stays locked for good.
  useEffect(() => {
    if (editor && editor.isEditable === disabled) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  if (!editor) {
    return null;
  }

  const openLinkPopover = () => {
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, ' ');
    const previousUrl = editor.getAttributes('link').href as string | undefined;

    linkSelectionRef.current = { from, to };
    initialLinkTextRef.current = selectedText;
    initialLinkHrefRef.current = previousUrl || '';
    setLinkText(selectedText);
    setLinkHref(previousUrl || '');
    setLinkError('');
    setLinkPopoverOpen(true);
  };

  const applyLink = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedUrl = normalizeLinkHref(linkHref);
    const selection = linkSelectionRef.current;

    if (!selection) {
      setLinkPopoverOpen(false);
      return;
    }

    if (normalizedUrl === null) {
      setLinkError('Links must use http, https, mailto, or tel.');
      return;
    }

    if (normalizedUrl === '') {
      editor.chain().focus().setTextSelection(selection).extendMarkRange('link').unsetLink().run();
      setLinkPopoverOpen(false);
      return;
    }

    const displayText = linkText.trim() || normalizedUrl;
    const hasSelection = selection.from !== selection.to;

    if (hasSelection && displayText === initialLinkTextRef.current) {
      editor
        .chain()
        .focus()
        .setTextSelection(selection)
        .extendMarkRange('link')
        .setLink({ href: normalizedUrl })
        .run();
    } else if (!hasSelection && initialLinkHrefRef.current && linkText.trim() === '') {
      editor
        .chain()
        .focus()
        .setTextSelection(selection)
        .extendMarkRange('link')
        .setLink({ href: normalizedUrl })
        .run();
    } else {
      const insertedTo = selection.from + displayText.length;

      editor
        .chain()
        .focus()
        .setTextSelection(selection)
        .insertContent(displayText)
        .setTextSelection({ from: selection.from, to: insertedTo })
        .setLink({ href: normalizedUrl })
        .run();
    }

    setLinkError('');
    setLinkPopoverOpen(false);
  };

  const handleImageUpload = () => {
    if (!onFileUpload) return;
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        try {
          if (onUploadStart) onUploadStart();
          await onFileUpload(file);
          // Don't insert into editor - parent component will handle showing as chips
        } catch (error) {
          console.error('Failed to upload image:', error);
        } finally {
          if (onUploadEnd) onUploadEnd();
        }
      }
    };
    input.click();
  };

  const handleFileAttachment = () => {
    if (!onFileUpload) return;
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx,image/*';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        try {
          if (onUploadStart) onUploadStart();
          await onFileUpload(file);
          // Don't insert into editor - parent component will handle showing as chips
        } catch (error) {
          console.error('Failed to upload file:', error);
        } finally {
          if (onUploadEnd) onUploadEnd();
        }
      }
    };
    input.click();
  };

  return (
    <div className={cn('border rounded-lg bg-background', className)}>
      {showToolbar && (
        <div className="flex items-center gap-1 p-2 border-b">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={disabled}
            data-active={editor.isActive('bold')}
          >
            <Bold className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={disabled}
            data-active={editor.isActive('italic')}
          >
            <Italic className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            disabled={disabled}
            data-active={editor.isActive('bulletList')}
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            disabled={disabled}
            data-active={editor.isActive('orderedList')}
          >
            <ListOrdered className="h-4 w-4" />
          </Button>
          <Popover open={linkPopoverOpen} onOpenChange={setLinkPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={openLinkPopover}
                disabled={disabled}
                data-active={editor.isActive('link')}
                title="Add or edit link"
              >
                <LinkIcon className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 p-3">
              <form onSubmit={applyLink} className="space-y-2">
                <Input
                  aria-label="Link text"
                  value={linkText}
                  onChange={(event) => setLinkText(event.target.value)}
                  placeholder="Text"
                />
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <LinkIcon className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      ref={linkHrefInputRef}
                      aria-label="Link URL"
                      value={linkHref}
                      onChange={(event) => {
                        setLinkHref(event.target.value);
                        setLinkError('');
                      }}
                      placeholder="Type or paste a link"
                      className="pl-8"
                    />
                  </div>
                  <Button
                    type="submit"
                    variant="ghost"
                    disabled={!linkHref.trim() && !initialLinkHrefRef.current}
                    className="shrink-0"
                  >
                    Apply
                  </Button>
                </div>
                {linkError && (
                  <p className="text-xs text-destructive">{linkError}</p>
                )}
              </form>
            </PopoverContent>
          </Popover>
          
          {onFileUpload && (
            <>
              <div className="w-px h-6 bg-border mx-1" />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={handleImageUpload}
                disabled={disabled}
                title="Upload image"
              >
                <ImageIcon className="h-4 w-4" />
              </Button>
              {enableFileAttachments && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={handleFileAttachment}
                  disabled={disabled}
                  title="Attach file"
                >
                  <Paperclip className="h-4 w-4" />
                </Button>
              )}
            </>
          )}
          {toolbarEnd && <div className="ml-auto flex items-center pl-2">{toolbarEnd}</div>}
        </div>
      )}
      <div className="p-3">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default RichTextEditor;

